import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import 'mapbox-gl/dist/mapbox-gl.css';
import { analyzeActivityWaterContent } from './utils/waterDetection';

// You'll need to set your Mapbox access token
// For now, I'll use the same token that's configured in the backend
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoiZGFuaWVsa2FsZG9yIiwiYSI6ImNtYjRyNTYzczA1emcya3FwdzhoMmhmdGIifQ.qkeEKxO6j5ugrpbdC06grA'//'pk.eyJ1Ijoic2FtdWVsY29sbGluMzMzMyIsImEiOiJjbWE0eTk0Ym8wY3d5MnRvbHcwYTN2bmxyIn0.uU_WIc4jMrwPqVBtBYZVBg';

// Check if mapboxgl is available
if (typeof mapboxgl === 'undefined') {
  console.error('Mapbox GL JS is not loaded!');
} else {
  console.log('Mapbox GL JS version:', mapboxgl.version);
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

const ActivityMap = ({ 
    activity, 
    width = 300, 
    height = 200, 
    interactive = false,
    onWaterAnalysis = null // New callback prop for water analysis results
}) => {
    const map = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [useStaticFallback, setUseStaticFallback] = useState(!interactive);
    const [mapContainer, setMapContainer] = useState(null);
    const [waterAnalysis, setWaterAnalysis] = useState(null);
    const [showAnalyzingIndicator, setShowAnalyzingIndicator] = useState(false);

    // Refs to control analysis state without causing re-renders
    const analysisTriggered = useRef(false);
    const isAnalyzing = useRef(false);

    // Callback ref to get the map container DOM element
    const mapContainerRef = useCallback((node) => {
        if (node !== null) {
            setMapContainer(node);
        }
    }, []);

    // Reset analysis state when the activity prop changes
    useEffect(() => {
        analysisTriggered.current = false;
        isAnalyzing.current = false;
        setShowAnalyzingIndicator(false);
        setWaterAnalysis(null);
        setIsLoading(true); // Reset loading state for new activity
    }, [activity.id]);

    // Function to perform water analysis, now memoized with stable dependencies
    const performWaterAnalysis = useCallback(async (mapSource) => {
        if (!activity || !onWaterAnalysis || analysisTriggered.current || isAnalyzing.current) {
            return;
        }
        
        analysisTriggered.current = true;
        isAnalyzing.current = true;
        setShowAnalyzingIndicator(true);

        try {
            const analysis = await analyzeActivityWaterContent(activity, mapSource);
            setWaterAnalysis(analysis);
            onWaterAnalysis(analysis);
            if (localStorage.getItem('waterDetectionDebug') === 'true') {
                console.log('ActivityMap: Water analysis complete:', analysis);
            }
        } catch (error) {
            console.error('ActivityMap: Water analysis failed:', error);
            analysisTriggered.current = false; // Allow re-triggering on failure
            if (onWaterAnalysis) {
                onWaterAnalysis({
                    waterPercentage: 0,
                    categories: { land: true, coastal: false, ocean: false, river: false },
                    recommendation: 'land',
                    error: error.message
                });
            }
        } finally {
            isAnalyzing.current = false;
            setShowAnalyzingIndicator(false);
        }
    }, [activity, onWaterAnalysis]);

    // Main effect for map initialization and rendering
    useEffect(() => {
        // If we're using static maps, skip all the interactive map setup
        if (!interactive) {
            setIsLoading(false);
            return; // Static map is rendered via JSX, not here
        }

        // Reset states for interactive mode
        setIsLoading(true);
        setHasError(false);
        setUseStaticFallback(false);

        if (!activity || !activity.map || !activity.map.summary_polyline) {
            setIsLoading(false);
            setHasError(true);
            return;
        }

        if (!mapContainer) {
            // Container not ready yet, will retry when mapContainer is set
            console.log('ActivityMap: Waiting for container...');
            return;
        }

        console.log('ActivityMap: Container ready, initializing interactive map for activity:', activity.id);

        // Set a timeout to fallback to static map if loading takes too long
        const loadingTimeout = setTimeout(() => {
          console.warn('ActivityMap: Map loading timeout, falling back to static map');
          setUseStaticFallback(true);
          setIsLoading(false);
        }, 5000); // 5 second timeout

        try {
            // Initialize map
            map.current = new mapboxgl.Map({
                container: mapContainer,
                style: 'samuelcollin3333/cmcyoppzj00ds01rf0gjpbt16', // Using default style to test
                // style: 'mapbox://styles/samuelcollin3333/cma6fxun5007a01rf1eog70n4', // Custom style
                center: [0, 0], // Will be updated based on route
                zoom: 12,
                interactive: false, // Disable interaction for preview
                attributionControl: false, // Remove attribution for cleaner look,
            });

            // Add style.load event listener as well
            map.current.once('style.load', () => {
                console.log('ActivityMap: Style loaded');
            });

            map.current.on('load', () => {
                clearTimeout(loadingTimeout); // Clear the timeout since map loaded successfully
                
                try {
                    console.log('ActivityMap: Map loaded, processing polyline');
                    // Decode the polyline
                    const coordinates = polyline.decode(activity.map.summary_polyline);
                    
                    if (!coordinates || coordinates.length === 0) {
                        throw new Error('No coordinates found in polyline');
                    }
                    
                    console.log(`ActivityMap: Decoded ${coordinates.length} coordinates`);
                    
                    // Convert to GeoJSON format (swap lat/lng to lng/lat)
                    const geojsonCoordinates = coordinates.map(coord => [coord[1], coord[0]]);
                    
                    // Create GeoJSON feature
                    const routeGeoJSON = {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: geojsonCoordinates
                        }
                    };

                    // Add source and layer
                    map.current.addSource('route', {
                        type: 'geojson',
                        data: routeGeoJSON
                    });

                    map.current.addLayer({
                        id: 'route',
                        type: 'line',
                        source: 'route',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#FC5200', // Strava orange
                            'line-width': 3,
                            'line-opacity': 0.8
                        }
                    });

                    // Fit map to route bounds
                    const bounds = new mapboxgl.LngLatBounds();
                    geojsonCoordinates.forEach(coord => bounds.extend(coord));
                    map.current.fitBounds(bounds, { padding: 20 });

                    console.log('ActivityMap: Map rendering complete');
                    setIsLoading(false);

                    // Perform water analysis after map is fully rendered
                    if (onWaterAnalysis) {
                        setTimeout(() => {
                            if (map.current && map.current.loaded()) {
                                performWaterAnalysis(map.current);
                            }
                        }, 500);
                    }

                } catch (error) {
                    console.error('Error rendering activity map:', error);
                    setUseStaticFallback(true);
                    setIsLoading(false);
                }
            });

            map.current.on('error', (e) => {
                clearTimeout(loadingTimeout);
                console.error('Mapbox error:', e);
                setUseStaticFallback(true);
                setIsLoading(false);
            });

            // Also listen for source data loading
            map.current.on('sourcedata', (e) => {
                if (e.isSourceLoaded) {
                    console.log('ActivityMap: Source data loaded for', e.sourceId);
                }
            });

        } catch (initError) {
            clearTimeout(loadingTimeout);
            console.error('Error initializing Mapbox map:', initError);
            setUseStaticFallback(true);
            setIsLoading(false);
        }

        // Cleanup
        return () => {
            clearTimeout(loadingTimeout);
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
        };
    }, [activity, mapContainer, interactive, performWaterAnalysis]);

    const containerStyle = {
        width: `${width}px`, 
        height: `${height}px`,
        position: 'relative'
    };

    // Generate static map URL as fallback
    const generateStaticMapUrl = useCallback(() => {
        if (!activity || !activity.map || !activity.map.summary_polyline) return null;
        
        try {
            const encodedPolyline = encodeURIComponent(activity.map.summary_polyline);
            const pathString = `path-5+FC5200-1(${encodedPolyline})`;
            const style = 'samuelcollin3333/cmcyoppzj00ds01rf0gjpbt16';
            return `https://api.mapbox.com/styles/v1/${style}/static/${pathString}/auto/${width}x${height}@2x?padding=20&attribution=false&logo=false&access_token=${MAPBOX_ACCESS_TOKEN}`;
        } catch (error) {
            console.error('Error generating static map URL:', error);
            return null;
        }
    }, [activity, width, height]);

    // Use a memoized value for the static map URL
    const staticMapUrl = useMemo(() => generateStaticMapUrl(), [generateStaticMapUrl]);

    // Effect for handling static map analysis
    useEffect(() => {
        if (!interactive && onWaterAnalysis && staticMapUrl) {
            // Since there's no reliable onLoad for background images, we use a timeout
            // The analysis is now triggered here instead of inside an img.onLoad event
            const timer = setTimeout(() => {
                // We need to create an image element to analyze
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => performWaterAnalysis(img);
                img.onerror = () => {
                    console.error('Static map image could not be loaded for analysis.');
                    analysisTriggered.current = false; // Allow retry
                };
                img.src = staticMapUrl;
            }, 500); // Small delay to ensure render
            
            return () => clearTimeout(timer);
        }
    }, [interactive, onWaterAnalysis, staticMapUrl, performWaterAnalysis]);

    if (isLoading) {
        return (
            <div style={{
                ...containerStyle,
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                fontSize: '12px'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid #e0e0e0',
                        borderTop: '2px solid #FC5200',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Loading map...</span>
                </div>
            </div>
        );
    }

    if (useStaticFallback || !interactive) {
        const url = staticMapUrl;
        if (url) {
            return (
                <div style={containerStyle}>
                    <img 
                        src={url} 
                        alt="Activity route" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        // The onLoad handler for analysis is now managed by the useEffect above
                        // to prevent it from firing on every re-render.
                        onError={() => setHasError(true)}
                    />
                    {showAnalyzingIndicator && (
                        <div style={{
                            position: 'absolute', top: '5px', right: '5px',
                            background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 8px',
                            borderRadius: '4px', fontSize: '10px'
                        }}>
                            Analyzing...
                        </div>
                    )}
                </div>
            );
        }
    }

    if (hasError || !activity || !activity.map || !activity.map.summary_polyline) {
        const isActivityWithoutRoute = activity && (!activity.map || !activity.map.summary_polyline);
        return (
            <div style={{
                ...containerStyle,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666',
                fontSize: '12px'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    textAlign: 'center'
                }}>
                    <span style={{ fontSize: '16px' }}>
                        {isActivityWithoutRoute ? '📍' : '🗺️'}
                    </span>
                    <span>
                        {isActivityWithoutRoute ? 'No route data' : 'No map data'}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div ref={mapContainerRef} style={containerStyle}>
            {showAnalyzingIndicator && (
                <div style={{
                    position: 'absolute', top: '5px', right: '5px',
                    background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 8px',
                    borderRadius: '4px', fontSize: '10px', zIndex: 1000
                }}>
                    Analyzing...
                </div>
            )}
            {waterAnalysis && (
                <div style={{
                    position: 'absolute', bottom: '5px', left: '5px',
                    background: 'rgba(0,0,0,0.7)', color: 'white', padding: '4px 8px',
                    borderRadius: '4px', fontSize: '10px', zIndex: 1000
                }}>
                    Water: {waterAnalysis.waterPercentage}%
                </div>
            )}
        </div>
    );
};

export default ActivityMap; 